import React, { useState, useRef } from 'react';
import { CompanyConfig } from '@/constants/cx-companies';
import { CXLanding } from './CXLanding';
import { CXPreCall } from './CXPreCall';
import { CXCall } from './CXCall';
import { CXFeedback } from './CXFeedback';
import { useCXConversation } from '@/hooks/useCXConversation';

export type ViewState = 'landing' | 'pre-call' | 'call' | 'feedback';

interface TranscriptEntry {
  source: 'ai' | 'user';
  message: string;
  timestamp: Date;
}

interface CXDemoProps {
  config: CompanyConfig;
  engine?: '11labs' | 'realtime';
}

export const CXDemo: React.FC<CXDemoProps> = ({ config, engine = '11labs' }) => {
  const [currentView, setCurrentView] = useState<ViewState>('landing');
  const [callDuration, setCallDuration] = useState<number>(0);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [shouldStartCall, setShouldStartCall] = useState(false);
  const startTimeRef = useRef<number>(Date.now());
  const timerRef = useRef<NodeJS.Timeout>();

  // Initialize conversation hook with transcript handling
  const {
    isConnected,
    isConnecting,
    isSpeaking,
    startConversation,
    endConversation,
  } = useCXConversation({
    engine,
    onConnect: () => {
      console.log('[CXDemo] Connected, starting timer');
      startTimeRef.current = Date.now();
      // Start timer
      timerRef.current = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    },
    onDisconnect: () => {
      console.log('[CXDemo] Disconnected, stopping timer');
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    },
    onTranscriptUpdate: (entry) => {
      console.log('[CXDemo] Transcript update:', entry);
      setTranscript(prev => [...prev, entry]);
    },
  });

  const handleStartCall = async () => {
    console.log('[CXDemo] Arrow clicked, moving to pre-call view');
    setCurrentView('pre-call');
    // Don't start conversation yet - wait for user to click "Start Call"
  };

  const handleBeginCall = async () => {
    console.log('[CXDemo] Start Call clicked, starting conversation and moving to call view');
    setShouldStartCall(true);
    setCurrentView('call');

    // Start the conversation when "Start Call" is clicked
    if (!isConnected && !isConnecting) {
      console.log(`[CXDemo] Starting conversation with ${engine} engine`);
      try {
        await startConversation(
          config.agentId,
          config.chat.initialAIMessage,
          config.id
        );
      } catch (error) {
        console.error('[CXDemo] Failed to start conversation:', error);
      }
    } else {
      console.log('[CXDemo] Already connected, conversation in progress');
    }
  };

  const handleEndCall = (duration: number) => {
    console.log('[CXDemo] Ending call, duration:', duration);
    setCallDuration(duration);
    setShouldStartCall(false);
    endConversation();
    setCurrentView('feedback');
  };

  const handleStartNewCall = async () => {
    // Reset transcript for new call
    setTranscript([]);
    setCallDuration(0);
    setShouldStartCall(true);
    setCurrentView('call');

    // Start a new conversation
    try {
      await startConversation(
        config.agentId,
        config.chat.initialAIMessage,
        config.id
      );
    } catch (error) {
      console.error('[CXDemo] Failed to start new conversation:', error);
    }
  };

  switch (currentView) {
    case 'landing':
      return <CXLanding config={config} onStartCall={handleStartCall} />;
    case 'pre-call':
      return (
        <CXPreCall
          config={config}
          onBeginCall={handleBeginCall}
        />
      );
    case 'call':
      return (
        <CXCall
          config={config}
          onEndCall={handleEndCall}
          transcript={transcript}
          isSpeaking={isSpeaking}
          isConnected={isConnected}
          callDuration={callDuration}
        />
      );
    case 'feedback':
      return <CXFeedback config={config} callDuration={callDuration} transcript={transcript} onStartNewCall={handleStartNewCall} />;
    default:
      return <CXLanding config={config} onStartCall={handleStartCall} />;
  }
};